import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  ChevronRight, 
  Zap, 
  ShieldCheck, 
  BarChart3, 
  Users, 
  ShoppingBag, 
  Smartphone, 
  Globe, 
  Lock, 
  RefreshCcw, 
  Layers,
  MessageSquare,
  Clock,
  Heart,
  Layout,
  Calendar,
  CheckCircle2,
  Printer,
  Database,
  Headphones
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePublic } from '../../context/PublicContext';
import SEO from '../../components/SEO';

const Categories = ["All", "Billing", "Inventory", "Management", "Security"];

const HeroCard = ({ image, title, description, delay }) => (
  <motion.div 
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay }}
    className="flex flex-col space-y-4"
  >
    <div className="relative aspect-[4/3] rounded-[2.5rem] overflow-hidden group shadow-lg">
      <img 
        src={image} 
        alt={title} 
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
    </div>
    <div className="space-y-2">
      <h3 className="text-xl font-bold text-[#1a1a1a]">{title}</h3>
      <p className="text-slate-500 text-sm leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

const ServiceCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div 
    layout
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    transition={{ duration: 0.4, delay }}
    className="bg-[#f8f9fa] rounded-[2.5rem] p-10 space-y-6 flex flex-col h-full hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-500 border border-transparent hover:border-emerald-100"
  >
    <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center text-white shadow-xl shadow-emerald-600/20">
      <Icon size={28} />
    </div>
    <div className="space-y-4 flex-grow">
      <h3 className="text-2xl font-bold text-[#1a1a1a] tracking-tight">{title}</h3>
      <p className="text-slate-500 leading-relaxed font-medium">{description}</p>
    </div>
    <Link to="/contact" className="flex items-center gap-2 text-slate-900 font-bold text-sm group w-fit">
      Get a Quote <ChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
    </Link>
  </motion.div>
);

const ServicesPage = () => {
  const { openDemoModal } = usePublic();
  const [activeCategory, setActiveCategory] = useState("All");

  const heroCards = [
    {
      title: "Retail POS Setup",
      description: "Professional implementation of high-speed checkout systems for your retail storefront.",
      image: "/assets/images/features/hero_1.png"
    },
    {
      title: "Inventory Audit",
      description: "Comprehensive stock tracking and automated reordering workflows to prevent stockouts.",
      image: "/assets/images/features/hero_2.png"
    },
    {
      title: "GST Compliance",
      description: "Automated tax calculations and GSTR-ready reporting to keep your business 100% compliant.",
      image: "/assets/images/features/hero_3.png"
    },
    {
      title: "Multi-Outlet Sync",
      description: "Real-time synchronization across all your branches for centralized business control.",
      image: "/assets/images/features/hero_4.png"
    }
  ];

  const services = [
    {
      category: "Billing",
      icon: Zap,
      title: "Quick Billing",
      description: "Generate professional GST invoices in seconds with our optimized retail checkout interface."
    },
    {
      category: "Inventory",
      icon: ShoppingBag,
      title: "Smart Inventory",
      description: "Track every item with precision using barcode integration and real-time stock level monitoring."
    },
    {
      category: "Management",
      icon: Users,
      title: "Staff Roles",
      description: "Assign specific permissions and monitor employee performance across multiple outlets securely."
    },
    {
      category: "Billing",
      icon: MessageSquare,
      title: "Digital Receipts",
      description: "Send eco-friendly invoices directly to customers via WhatsApp or Email instantly after checkout."
    },
    {
      category: "Management",
      icon: BarChart3,
      title: "Expense Tracker",
      description: "Monitor your overheads, purchase orders, and daily expenses to calculate accurate net profit."
    },
    {
      category: "Management",
      icon: Heart,
      title: "Customer CRM",
      description: "Build lasting loyalty with integrated customer purchase history and automated reward points."
    },
    {
      category: "Billing",
      icon: Printer,
      title: "Hardware Support",
      description: "Seamlessly connect thermal printers, barcode scanners, and digital weighing scales."
    },
    {
      category: "Security",
      icon: Database,
      title: "Cloud Backup",
      description: "Your critical business data is encrypted and backed up 24/7 on our secure cloud servers."
    },
    {
      category: "Security",
      icon: Headphones,
      title: "24/7 Support",
      description: "Expert technical assistance is just a call away to ensure your business never stops running."
    }
  ];

  const filteredServices = activeCategory === "All" 
    ? services 
    : services.filter(s => s.category === activeCategory);

  return (
    <div className="min-h-screen bg-white font-lexend">
      <SEO
        title="Services - Gio Billing"
        description="Explore the professional POS, inventory, and GST management services offered by Gio Billing for your business."
        url="/services"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto space-y-20">
          <div className="max-w-4xl space-y-8">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-6xl md:text-8xl font-bold text-[#1a1a1a] leading-[1] tracking-tighter"
            >
              Smart Billing, <br />
              <span className="text-emerald-500">Better Business</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-slate-500 text-xl md:text-2xl leading-relaxed max-w-3xl font-medium"
            >
              Empower your retail operations with Gio Billing's comprehensive suite of POS, 
              Inventory, and GST management services—all in one place!
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {heroCards.map((card, idx) => (
              <HeroCard key={idx} {...card} delay={0.2 + (idx * 0.1)} />
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto space-y-16">
          <div className="space-y-6">
            <h4 className="text-emerald-600 font-bold uppercase tracking-[0.2em] text-sm">Services</h4>
            <h2 className="text-5xl md:text-7xl font-bold text-[#1a1a1a] leading-[1.1] tracking-tight">
              Comprehensive Solutions for <br className="hidden md:block" />
              Modern Retail Success.
            </h2>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-wrap gap-4 pt-4">
            {Categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-8 py-3 rounded-full font-bold transition-all duration-300 text-sm ${
                  activeCategory === cat 
                    ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-600/20' 
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <motion.div 
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
          >
            <AnimatePresence mode="popLayout">
              {filteredServices.map((service, idx) => (
                <ServiceCard key={service.title} {...service} delay={idx * 0.05} />
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-[700px] flex items-center justify-center px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/assets/images/features/cta_bg.png" 
            alt="CTA Background" 
            className="w-full h-full object-cover brightness-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        </div>

        <div className="relative z-10 text-center space-y-10 max-w-5xl">
          <h2 className="text-5xl md:text-8xl font-bold text-white leading-tight tracking-tighter">
            Ready to streamline your <br className="hidden md:block" />
            business with Gio Billing?
          </h2>
          <div className="flex flex-wrap justify-center gap-6">
            <button 
              onClick={openDemoModal}
              className="bg-white text-[#1a1a1a] px-12 py-5 rounded-full font-bold text-xl hover:bg-emerald-600 hover:text-white transition-all shadow-2xl active:scale-95"
            >
              Get started
            </button>
            <Link 
              to="/contact" 
              className="bg-white/10 backdrop-blur-xl border border-white/20 text-white px-12 py-5 rounded-full font-bold text-xl hover:bg-white/20 transition-all flex items-center gap-3 active:scale-95"
            >
              Contact Sales <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
